import { isAxiosError, isCancel } from 'axios';
import { axiosInstance } from '../utils/axiosApp';

export type OrgRole = 'admin' | 'teacher';
export type OrgSummary = {
  org_id: string;
  slug: string;
  name: string;
  logo_url?: string | null;
  role: OrgRole;
};
export type Organization = Omit<OrgSummary, 'role'> & {
  revision: string;
  logo_url: string | null;
  type: string;
  address: string | null;
  city: string | null;
  country: string | null;
};
export type OrganizationMember = {
  account_id: string;
  name: string | null;
  email: string | null;
  role: OrgRole;
  is_me: boolean;
};
export type OrganizationInvitation = {
  invitation_id: string;
  email: string;
  role: OrgRole;
  email_queued: boolean | null;
};
export type OrganizationAssignment = {
  invited: boolean;
  email_queued: boolean | null;
};
export const organizationApiPath = (id: string) =>
  `/api/orgs/${encodeURIComponent(id)}`;

export async function listMyOrgs(): Promise<OrgSummary[]> {
  const { data } = await axiosInstance().get<OrgSummary[]>('/api/orgs', {
    params: { role: 'admin' },
    timeout: 15_000,
  });
  if (!Array.isArray(data)) throw new Error('Invalid organization list');
  return data.filter((org) => org.role === 'admin');
}

export function needsWriteReconciliation(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  return (
    !isCancel(error) &&
    (status === undefined || status === 408 || status === 409 || status >= 500)
  );
}

// A read alone cannot settle an outstanding write. The server advances the
// document revision before returning a snapshot that older writes cannot alter.
export async function reconcileOrganization(
  orgId: string
): Promise<Organization> {
  const { data } = await axiosInstance().post<Organization>(
    `${organizationApiPath(orgId)}/reconcile`,
    undefined,
    { timeout: 15_000 }
  );
  return data;
}
